import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const emptyForm = { name: "", color: "", material: "", image_url: "", price_per_meter: 0 };

export default function FabricsPage() {
  const [fabrics, setFabrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = () => {
    setLoading(true);
    axios.get(`${API}/fabrics`).then((r) => setFabrics(r.data)).catch(console.error).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openAdd = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (f) => { setEditing(f); setForm({ name: f.name, color: f.color, material: f.material, image_url: f.image_url, price_per_meter: f.price_per_meter }); setDialogOpen(true); };

  const save = async () => {
    try {
      if (editing) { await axios.put(`${API}/fabrics/${editing.id}`, form); toast.success("Fabric updated"); }
      else { await axios.post(`${API}/fabrics`, form); toast.success("Fabric added"); }
      setDialogOpen(false); load();
    } catch { toast.error("Failed to save"); }
  };

  const del = async (id) => {
    try { await axios.delete(`${API}/fabrics/${id}`); toast.success("Fabric deleted"); load(); }
    catch { toast.error("Failed to delete"); }
  };

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="fabrics-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-['Space_Mono'] text-3xl tracking-tight uppercase text-[#5C4D24]">Fabrics</h2>
          <p className="text-sm text-[#8B7536] mt-1">Manage your fabric inventory</p>
        </div>
        <button onClick={openAdd} data-testid="add-fabric-btn" className="flex items-center gap-2 px-6 py-3 bg-[#9F7D23] text-white font-['Space_Mono'] text-xs tracking-widest uppercase hover:bg-[#8A6D1F] transition-all active:scale-95">
          <Plus className="w-4 h-4" /> Add Fabric
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 text-[#9F7D23] animate-spin" /></div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="fabrics-grid">
          {fabrics.map((fab) => (
            <div key={fab.id} className="bg-white border border-[#D6D6CF] overflow-hidden group hover:border-[#9F7D23] transition-colors" data-testid={`fabric-card-${fab.id}`}>
              <div className="aspect-square bg-[#EBEBE6] overflow-hidden">
                {fab.image_url ? (
                  <img src={fab.image_url} alt={fab.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[#D6D6CF] font-['Space_Mono'] text-xs uppercase">No Image</div>
                )}
              </div>
              <div className="p-4">
                <p className="font-['Space_Mono'] text-sm uppercase tracking-wider text-[#5C4D24]">{fab.name}</p>
                <p className="text-xs text-[#8B7536] mt-1">{fab.color} / {fab.material}</p>
                <p className="font-['Space_Mono'] text-sm text-[#9F7D23] mt-2">{fab.price_per_meter} AED/m</p>
                <div className="flex gap-2 mt-3">
                  <button onClick={() => openEdit(fab)} data-testid={`edit-fabric-${fab.id}`} className="flex items-center gap-1 px-3 py-1.5 border border-[#D6D6CF] text-[#5C4D24] font-['Space_Mono'] text-[10px] uppercase tracking-widest hover:border-[#9F7D23] transition-colors">
                    <Pencil className="w-3 h-3" /> Edit
                  </button>
                  <button onClick={() => del(fab.id)} data-testid={`delete-fabric-${fab.id}`} className="flex items-center gap-1 px-3 py-1.5 border border-[#D6D6CF] text-[#9F3A3A] font-['Space_Mono'] text-[10px] uppercase tracking-widest hover:border-[#9F3A3A] transition-colors">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-none border-[#D6D6CF] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Space_Mono'] text-lg uppercase tracking-tight text-[#5C4D24]">
              {editing ? "Edit Fabric" : "Add Fabric"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5 pt-2">
            <div>
              <label className="font-['Space_Mono'] text-[10px] tracking-widest uppercase text-[#8B7536] block mb-2">Name *</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="fabric-name-input" className="tinkah-input" placeholder="Fabric name" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="font-['Space_Mono'] text-[10px] tracking-widest uppercase text-[#8B7536] block mb-2">Color</label>
                <input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} data-testid="fabric-color-input" className="tinkah-input" placeholder="Gold" />
              </div>
              <div>
                <label className="font-['Space_Mono'] text-[10px] tracking-widest uppercase text-[#8B7536] block mb-2">Material</label>
                <input value={form.material} onChange={(e) => setForm({ ...form, material: e.target.value })} data-testid="fabric-material-input" className="tinkah-input" placeholder="100% Silk" />
              </div>
            </div>
            <div>
              <label className="font-['Space_Mono'] text-[10px] tracking-widest uppercase text-[#8B7536] block mb-2">Image URL</label>
              <input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} data-testid="fabric-image-input" className="tinkah-input" placeholder="https://..." />
            </div>
            <div>
              <label className="font-['Space_Mono'] text-[10px] tracking-widest uppercase text-[#8B7536] block mb-2">Price per Meter (AED)</label>
              <input type="number" value={form.price_per_meter || ""} onChange={(e) => setForm({ ...form, price_per_meter: parseFloat(e.target.value) || 0 })} data-testid="fabric-price-input" className="tinkah-input" min="0" />
            </div>
            <button onClick={save} disabled={!form.name} data-testid="save-fabric-btn" className="w-full py-3 bg-[#9F7D23] text-white font-['Space_Mono'] text-xs tracking-widest uppercase hover:bg-[#8A6D1F] disabled:opacity-50 transition-all active:scale-95">
              {editing ? "Update Fabric" : "Add Fabric"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
